import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./DetailsPage.css";
import { API_BASE } from "../api/config";

const API_KEY = "b4dff0c5e72a4b6f9c342f8e90b1c2d8";

const STATUS_OPTIONEN = [
  { value: "aktiv", label: "Nur aktiv" },
  { value: "alle", label: "Alle" },
  { value: "privat", label: "Privat" },
  { value: "nicht_relevant", label: "Nicht relevant" },
  { value: "archiv", label: "Archiv" },
];

const STATUS_FARBEN = {
  aktiv: { bg: "var(--color-success-bg, #e8f5e9)", fg: "var(--color-success, #2e7d32)" },
  privat: { bg: "var(--color-warning-bg, #fff3e0)", fg: "var(--color-warning, #ed6c02)" },
  nicht_relevant: { bg: "var(--color-gray-100, #eee)", fg: "var(--color-gray-600, #666)" },
  archiv: { bg: "var(--color-info-bg, #e3f2fd)", fg: "var(--color-info, #1976d2)" },
};

const DetailsPage = () => {
  const { konto } = useParams(); // Holt das Auftragskonto aus der URL
  const [daten, setDaten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("aktiv");
  const [suche, setSuche] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkSaving, setBulkSaving] = useState(false);
  const [hinweis, setHinweis] = useState("");
  // Regel-Dialog nach Bulk-Status (privat/nicht_relevant/archiv)
  const [regelDialog, setRegelDialog] = useState(null); // {status, absender:[], vzVorschlag}
  const navigate = useNavigate();

  const fetchDaten = useCallback(async (filter) => {
    try {
      setLoading(true);
      setError(null);
      const statusParam = filter || statusFilter;
      const response = await fetch(
        `${API_BASE}/database/get_daten?auftragskonto=${konto}&status=${encodeURIComponent(statusParam)}`,
        { headers: { "X-API-Key": API_KEY } }
      );

      if (!response.ok) {
        throw new Error("Fehler beim Abrufen der Daten");
      }

      const data = await response.json();
      const rows = Array.isArray(data) ? data : [];

      // ✅ Daten transformieren: Auftragskonto mit großem "DE", Datum umwandeln, Beträge formatieren
      const transformedData = rows.map((eintrag) => ({
        ...eintrag,
        Auftragskonto: (eintrag.Auftragskonto || "").replace(/^de/, "DE"),
        Buchungstag: formatDate(eintrag.Buchungstag),
        Valutadatum: formatDate(eintrag.Valutadatum),
        Betrag: formatAmount(eintrag.Betrag),
        BetragWert: parseFloat(eintrag.Betrag) || 0,
        SaldoAnzeige: eintrag.Saldo === "" || eintrag.Saldo == null ? "" : formatAmount(eintrag.Saldo),
        IDAnzeige: eintrag.ID === "" || eintrag.ID == null ? "" : String(parseInt(eintrag.ID, 10)),
      }));

      setDaten(transformedData);
      setSelectedIds(new Set());
    } catch (err) {
      setError(err.message);
      setDaten([]);
    } finally {
      setLoading(false);
    }
  }, [konto, statusFilter]);

  useEffect(() => { fetchDaten(statusFilter); /* eslint-disable-next-line */ }, [statusFilter]);

  // 🔹 Funktion zur Formatierung von Datumswerten (`YYYY-MM-DD` → `TT.MM.JJJJ`)
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const parts = dateString.split("-");
    return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : dateString;
  };

  // 🔹 Funktion zur Formatierung von Beträgen (`1000000.50` → `1.000.000,50`)
  const formatAmount = (amount) => {
    if (!amount) return "0,00";
    const floatAmount = parseFloat(amount);
    return floatAmount.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  /* Client-seitige Textsuche über die sichtbaren Buchungen */
  const sucheLower = suche.trim().toLowerCase();
  const gefiltert = !sucheLower
    ? daten
    : daten.filter((d) =>
        [d.Verwendungszweck, d["Begünstigter"], d.Buchungstext, d.Betrag, d.Buchungstag]
          .map((v) => (v ?? "").toString().toLowerCase())
          .some((v) => v.includes(sucheLower))
      );

  const alleSichtbarenIds = gefiltert.map((d) => d.db_id).filter(Boolean);
  const alleGewaehlt = alleSichtbarenIds.length > 0 && alleSichtbarenIds.every((id) => selectedIds.has(id));

  const toggleAlle = () => {
    setSelectedIds(alleGewaehlt ? new Set() : new Set(alleSichtbarenIds));
  };

  /* Bulk: Status für die Auswahl setzen; bei privat/nicht_relevant/archiv
     danach den Regel-Dialog anbieten (künftige Umsätze automatisch labeln). */
  const setzeStatus = async (status) => {
    if (selectedIds.size === 0) return;
    setBulkSaving(true);
    setHinweis("");
    try {
      const ausgewaehlt = daten.filter((d) => selectedIds.has(d.db_id));
      const r = await fetch(`${API_BASE}/database/daten/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-API-Key": API_KEY },
        body: JSON.stringify({ ids: [...selectedIds], status }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      setHinweis(`${j.geaendert} Umsatz/Umsätze auf "${status.replace("_", " ")}" gesetzt.`);
      if (status !== "aktiv") {
        const clean = (v) => {
          const s = (v ?? "").toString().trim();
          return s.toLowerCase() === "nan" ? "" : s;
        };
        const absender = [...new Set(ausgewaehlt.map((d) => clean(d["Begünstigter"])).filter(Boolean))];
        const ibans = [...new Set(ausgewaehlt.map((d) => clean(d["Kontonummer/IBAN"])).filter(Boolean))];
        const vz = clean(ausgewaehlt.find((d) => clean(d.Verwendungszweck))?.Verwendungszweck || "");
        setRegelDialog({
          status,
          absender,
          regel: {
            beguenstigter: absender.length === 1 ? absender[0] : "",
            verwendungszweck: vz.slice(0, 60),
            kontonummer_iban: ibans.length === 1 ? ibans[0] : "",
          },
        });
      }
      await fetchDaten(statusFilter);
    } catch (e) {
      setError(`Status setzen fehlgeschlagen: ${e.message}`);
    } finally {
      setBulkSaving(false);
    }
  };

  /* Regel mit den ausgefüllten Kriterien anlegen (leere werden ignoriert,
     mehrere = alle müssen zutreffen) und sofort auf DIESES Konto anwenden. */
  const legeRegelAn = async () => {
    const regel = regelDialog.regel;
    const beguenstigter = (regel.beguenstigter || "").trim();
    const verwendungszweck = (regel.verwendungszweck || "").trim();
    const kontonummer_iban = (regel.kontonummer_iban || "").trim();
    if (!beguenstigter && !verwendungszweck && !kontonummer_iban) {
      setError("Mindestens ein Kriterium ausfüllen (Absender, Verwendungszweck oder IBAN).");
      return;
    }
    try {
      const r = await fetch(`${API_BASE}/database/status_regeln`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ beguenstigter, verwendungszweck, kontonummer_iban, status: regelDialog.status }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      // Regel rückwirkend anwenden — nur auf dieses Konto (schnell); andere
      // Konten werden bei ihrem nächsten Import/Sync gelabelt
      await fetch(`${API_BASE}/database/status_regeln/sync?iban=${encodeURIComponent(konto)}`, { method: "POST" });
      const teile = [
        beguenstigter && `Absender enthält "${beguenstigter}"`,
        verwendungszweck && `Verwendungszweck enthält "${verwendungszweck}"`,
        kontonummer_iban && `IBAN enthält "${kontonummer_iban}"`,
      ].filter(Boolean).join(" UND ");
      setHinweis(`Regel angelegt und rückwirkend angewendet: ${teile} → ${regelDialog.status.replace("_", " ")}.`);
      setRegelDialog(null);
      await fetchDaten(statusFilter);
    } catch (e) {
      setError(`Regel anlegen fehlgeschlagen: ${e.message}`);
    }
  };

  const setRegelFeld = (feld, wert) =>
    setRegelDialog((prev) => ({ ...prev, regel: { ...prev.regel, [feld]: wert } }));

  /* "nan"/leere Werte aus alten Importen nicht anzeigen */
  const zeigeText = (v) => {
    const s = (v ?? "").toString().trim();
    return s.toLowerCase() === "nan" ? "" : s;
  };

  const statusBadge = (status) => {
    const f = STATUS_FARBEN[status] || STATUS_FARBEN.aktiv;
    return (
      <span style={{ background: f.bg, color: f.fg, padding: "2px 8px", borderRadius: 10, fontSize: "0.75rem", fontWeight: 600, whiteSpace: "nowrap" }}>
        {(status || "aktiv").replace("_", " ")}
      </span>
    );
  };

  return (
    <div className="details-page">
      <h1>Details für Konto: {konto.replace(/^de/, "DE")}</h1>

      {/* Status-Filter + Bulk-Leiste */}
      <div className="details-toolbar">
        <label style={{ fontSize: "0.85rem", fontWeight: 600 }}>Anzeige:</label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          {STATUS_OPTIONEN.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <input
          type="search"
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
          placeholder="Suchen (Verwendungszweck, Begünstigter, Betrag...)"
          style={{ padding: "6px 10px", borderRadius: 6, minWidth: 280 }}
        />
        {selectedIds.size > 0 && (
          <>
            <span style={{ fontSize: "0.85rem" }}>{selectedIds.size} ausgewählt — Status setzen:</span>
            {["aktiv", "privat", "nicht_relevant", "archiv"].map((s) => (
              <button
                key={s}
                disabled={bulkSaving}
                onClick={() => setzeStatus(s)}
                style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid var(--color-gray-300, #ccc)", background: STATUS_FARBEN[s].bg, color: STATUS_FARBEN[s].fg, cursor: "pointer", fontWeight: 600, fontSize: "0.8rem" }}
              >
                {s.replace("_", " ")}
              </button>
            ))}
          </>
        )}
        {!loading && !error && (
          <span className="details-anzahl">
            {sucheLower ? `${gefiltert.length} von ${daten.length}` : daten.length} Buchungen
          </span>
        )}
      </div>
      {hinweis && <p style={{ fontSize: "0.85rem", color: "var(--color-success, #2e7d32)" }}>{hinweis}</p>}

      {loading && <p>Lade Daten...</p>}
      {error && <p className="error">{error}</p>}

      {!loading && !error && daten.length > 0 ? (
        <table className="details-special-table">
          <colgroup>
            <col style={{ width: 36 }} />
            <col style={{ width: 60 }} />
            <col style={{ width: 100 }} />
            <col style={{ width: 100 }} />
            <col style={{ width: 130 }} />
            <col />
            <col style={{ width: "20%" }} />
            <col style={{ width: 100 }} />
            <col style={{ width: 110 }} />
            <col style={{ width: 110 }} />
            <col style={{ width: 110 }} />
            <col style={{ width: 100 }} />
            <col style={{ width: 90 }} />
            <col style={{ width: 100 }} />
          </colgroup>
          <thead>
            <tr>
              <th>
                <input type="checkbox" checked={alleGewaehlt} onChange={toggleAlle} title="Alle auswählen" />
              </th>
              <th style={{ textAlign: "right" }} title="Laufende Nummer — privat/nicht relevant bekommen keine">ID</th>
              <th>Buchungstag</th>
              <th>Valutadatum</th>
              <th>Buchungstext</th>
              <th>Verwendungszweck</th>
              <th>Begünstigter</th>
              <th style={{ textAlign: "right" }}>Betrag (€)</th>
              <th style={{ textAlign: "right" }} title="Kumuliert ohne privat/nicht relevant">Saldo (€)</th>
              <th title="aus STRG-Regeln">KurzName</th>
              <th title="aus STRG-Regeln">Kategorie</th>
              <th title="aus STRG-Regeln">SubKat</th>
              <th title="aus STRG-Regeln">Kst</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {gefiltert.map((eintrag, index) => (
              <tr key={eintrag.db_id || index} style={selectedIds.has(eintrag.db_id) ? { outline: "1px solid var(--color-info, #1976d2)" } : undefined}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(eintrag.db_id)}
                    onChange={() => toggleSelect(eintrag.db_id)}
                  />
                </td>
                <td className="betrag">{eintrag.IDAnzeige}</td>
                <td>{eintrag.Buchungstag}</td>
                <td>{eintrag.Valutadatum}</td>
                <td title={zeigeText(eintrag.Buchungstext)}>{zeigeText(eintrag.Buchungstext)}</td>
                <td title={zeigeText(eintrag.Verwendungszweck)}>{zeigeText(eintrag.Verwendungszweck)}</td>
                <td title={zeigeText(eintrag["Begünstigter"])}>{zeigeText(eintrag["Begünstigter"])}</td>
                <td className={`betrag ${eintrag.BetragWert < 0 ? "neg" : "pos"}`}>{eintrag.Betrag}</td>
                <td className="betrag">{eintrag.SaldoAnzeige}</td>
                <td title={zeigeText(eintrag.KurzName)}>{zeigeText(eintrag.KurzName)}</td>
                <td title={zeigeText(eintrag.Kategorie)}>{zeigeText(eintrag.Kategorie)}</td>
                <td title={zeigeText(eintrag.SubKat)}>{zeigeText(eintrag.SubKat)}</td>
                <td title={zeigeText(eintrag.Kst)}>{zeigeText(eintrag.Kst)}</td>
                <td>{statusBadge(eintrag.Status)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        !loading && <p>Keine Buchungen für diesen Filter gefunden.</p>
      )}

      <button className="back-button" onClick={() => navigate("/data")}>Zurück zur Übersicht</button>

      {/* Regel-Dialog: künftige Umsätze automatisch labeln */}
      {regelDialog && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 5000 }}
          onClick={() => setRegelDialog(null)}
        >
          <div
            style={{ background: "var(--bg-card, #fff)", color: "var(--color-gray-800, #222)", borderRadius: 10, padding: 22, width: 480, maxWidth: "94vw", boxShadow: "0 12px 40px rgba(0,0,0,0.4)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 6px" }}>Regel anlegen?</h3>
            <p style={{ margin: "0 0 14px", fontSize: "0.85rem", color: "var(--color-gray-600, #666)" }}>
              Zukünftige Umsätze automatisch auf "{regelDialog.status.replace("_", " ")}" setzen.
              Leere Felder werden ignoriert — bei mehreren Feldern müssen alle zutreffen.
            </p>

            <div style={{ fontSize: "0.8rem", fontWeight: 600, marginBottom: 4 }}>Absender (Begünstigter) enthält:</div>
            <input
              style={{ width: "100%", padding: "7px 10px", marginBottom: 6, borderRadius: 6, border: "1px solid var(--color-gray-300, #ccc)" }}
              value={regelDialog.regel.beguenstigter}
              onChange={(e) => setRegelFeld("beguenstigter", e.target.value)}
              placeholder="leer = egal"
            />
            {regelDialog.absender.length > 1 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
                {regelDialog.absender.slice(0, 8).map((a) => (
                  <button
                    key={a}
                    onClick={() => setRegelFeld("beguenstigter", a)}
                    title="In das Absender-Feld übernehmen"
                    style={{ padding: "3px 10px", borderRadius: 12, border: "1px solid var(--color-gray-300, #ccc)", background: "transparent", cursor: "pointer", color: "inherit", fontSize: "0.75rem", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    {a}
                  </button>
                ))}
              </div>
            )}

            <div style={{ fontSize: "0.8rem", fontWeight: 600, margin: "6px 0 4px" }}>Verwendungszweck enthält:</div>
            <input
              style={{ width: "100%", padding: "7px 10px", marginBottom: 10, borderRadius: 6, border: "1px solid var(--color-gray-300, #ccc)" }}
              value={regelDialog.regel.verwendungszweck}
              onChange={(e) => setRegelFeld("verwendungszweck", e.target.value)}
              placeholder="leer = egal, z. B. VISA Debitkartenumsatz"
            />

            <div style={{ fontSize: "0.8rem", fontWeight: 600, marginBottom: 4 }}>Gegen-IBAN enthält:</div>
            <input
              style={{ width: "100%", padding: "7px 10px", marginBottom: 16, borderRadius: 6, border: "1px solid var(--color-gray-300, #ccc)", fontFamily: "monospace" }}
              value={regelDialog.regel.kontonummer_iban}
              onChange={(e) => setRegelFeld("kontonummer_iban", e.target.value)}
              placeholder="leer = egal"
            />

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={legeRegelAn}
                style={{ padding: "7px 14px", borderRadius: 6, border: "none", background: "var(--color-success, #2e7d32)", color: "#fff", cursor: "pointer", fontWeight: 600 }}
              >
                Regel anlegen
              </button>
              <button
                onClick={() => setRegelDialog(null)}
                style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid var(--color-gray-300, #ccc)", background: "transparent", cursor: "pointer", color: "inherit" }}
              >
                Keine Regel — nur diese Umsätze
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DetailsPage;
